import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import SignatureCanvas from "react-signature-canvas";

function typedSignatureToDataUrl(text) {
  if (!text || !String(text).trim()) return null;
  const canvas = document.createElement("canvas");
  canvas.width = 400;
  canvas.height = 160;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#f8f9fa";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#1a1a1a";
  ctx.font =
    'italic 42px "Dancing Script", "Brush Script MT", "Segoe Script", cursive';
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(text).trim(), canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL("image/png");
}

/**
 * e-Source signature control — same Draw / Type capture pattern as ISF e-signature.
 * Value is a PNG data URL (or empty string).
 */
export default function ESourceSignatureCapture({
  id,
  value = "",
  disabled = false,
  onChange,
  label = "Signature",
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("draw");
  const [typed, setTyped] = useState("");
  const canvasRef = useRef(null);
  const hasValue =
    typeof value === "string" &&
    value.startsWith("data:image") &&
    value.length > 32;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const openCapture = () => {
    if (disabled) return;
    setMode("draw");
    setTyped("");
    setOpen(true);
  };

  const clearValue = () => {
    if (disabled) return;
    onChange?.("");
  };

  const clearPad = () => {
    if (mode === "draw") {
      canvasRef.current?.clear?.();
    } else {
      setTyped("");
    }
  };

  const applySignature = () => {
    let dataUrl = null;
    if (mode === "draw") {
      if (canvasRef.current?.isEmpty?.()) return;
      dataUrl = canvasRef.current.toDataURL("image/png");
    } else {
      dataUrl = typedSignatureToDataUrl(typed);
      if (!dataUrl) return;
    }
    onChange?.(dataUrl);
    setOpen(false);
    setTyped("");
  };

  return (
    <div className="esource-form-signature" id={id}>
      {hasValue ? (
        <div className="esource-form-signature__preview">
          <img src={value} alt={`${label} preview`} />
          {!disabled ? (
            <div className="esource-form-signature__actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={openCapture}
              >
                Re-sign
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={clearValue}
              >
                Clear
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          className="esource-form-signature__add"
          disabled={disabled}
          onClick={openCapture}
        >
          <i className="fas fa-pen-fancy" aria-hidden="true" />
          <span>Add signature</span>
        </button>
      )}

      {open
        ? ReactDOM.createPortal(
            <div
              className="esource-form-signature-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Capture signature"
              onClick={(e) => {
                if (e.target === e.currentTarget) setOpen(false);
              }}
            >
              <div
                className="esource-form-signature-modal__dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="esource-form-signature-modal__header">
                  <h5 className="esource-form-signature-modal__title">
                    <i className="fas fa-pen-fancy me-2" aria-hidden="true" />
                    E-Signature
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setOpen(false)}
                  />
                </div>
                <div className="esource-form-signature-modal__body">
                  <div className="esource-form-signature-modal__modes">
                    <button
                      type="button"
                      className={`btn btn-sm${
                        mode === "draw"
                          ? " btn-primary"
                          : " btn-outline-secondary"
                      }`}
                      onClick={() => setMode("draw")}
                    >
                      <i className="fas fa-pen me-1" aria-hidden="true" />
                      Draw
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm${
                        mode === "type"
                          ? " btn-primary"
                          : " btn-outline-secondary"
                      }`}
                      onClick={() => setMode("type")}
                    >
                      <i className="fas fa-keyboard me-1" aria-hidden="true" />
                      Type
                    </button>
                  </div>

                  {mode === "draw" ? (
                    <>
                      <p className="esource-form-signature-modal__hint">
                        Draw your signature in the box below.
                      </p>
                      <div className="esource-form-signature-modal__pad">
                        <SignatureCanvas
                          ref={canvasRef}
                          canvasProps={{
                            className: "esource-form-signature-modal__canvas",
                            style: { width: "100%", height: "160px" },
                          }}
                          penColor="#1a1a1a"
                          backgroundColor="#f8f9fa"
                          clearOnResize={false}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="esource-form-signature-modal__hint">
                        Type your signature in the box below.
                      </p>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. John Smith"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        aria-label="Type your signature"
                        autoFocus
                      />
                      {typed.trim() ? (
                        <div className="esource-form-signature-modal__typed">
                          {typed.trim()}
                        </div>
                      ) : null}
                    </>
                  )}

                  <div className="esource-form-signature-modal__footer">
                    <div className="esource-form-signature-modal__footer-right">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={clearPad}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={applySignature}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
